import {
  buildForest,
  flattenForest,
  toTeamNode,
  type UserListing,
} from "@contract-manager/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ApiClient, UserTeamMembership } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { describeError, isSignedOutError } from "../auth/authErrors";
import { useCurrentUser } from "../auth/CurrentUser";
import { AdminSourceBadges } from "../components/AdminBadge";
import { Alert } from "../components/Alert";
import { ConfirmDialog } from "../components/Dialog";
import { useI18n } from "../i18n/I18n";
import { useDebouncedValue } from "../useDebouncedValue";
import { filterUsers } from "../userFilter";

/** How long typing must pause before the list is narrowed. */
export const USER_FILTER_DELAY_MS = 250;

/**
 * ユーザー管理: everyone who has signed in, and which teams they are on.
 *
 * Nobody is created here. A person appears the first time they sign in and
 * open the app, and they start in no team — joining one is a decision made
 * here, from the person, rather than by typing an address into a team.
 *
 * Everyone can look; changing who is on which team is for administrators.
 */
export function UserAdminPage({ api }: { api: ApiClient }) {
  const auth = useAuth();
  const { t, count } = useI18n();
  const { isAdmin } = useCurrentUser();
  const [users, setUsers] = useState<UserListing[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [query, setQuery] = useState("");
  const settledQuery = useDebouncedValue(query, USER_FILTER_DELAY_MS);
  const shown = useMemo(
    () => filterUsers(users, settledQuery),
    [users, settledQuery],
  );
  const isFiltering = shown.length !== users.length;

  const load = useCallback(async () => {
    try {
      setUsers((await api.listUsers()).items);
      setError(null);
    } catch (caught) {
      if (isSignedOutError(caught)) {
        auth.reportSignedOut();
        return;
      }
      setError(describeError(caught, t));
    } finally {
      setReady(true);
    }
  }, [api, auth, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const current = users.find((user) => user.email === selected) ?? null;

  return (
    <div className="ds-rows --gap-medium">
      <div className="ds-cols --baseline --gap-small">
        <h1>{t("users.title")}</h1>
        <Link to="/teams" className="ds-button --plain --small">
          <span>{t("users.toTeams")}</span>
        </Link>
      </div>

      {error !== null && <Alert tone="failed">{error}</Alert>}

      <div className="app-master-detail">
        <section className="ds-card">
          <div className="ds-cols --spaced --baseline">
            <h2 className="ds-text --large">{t("users.heading")}</h2>
            {ready && (
              <span className="ds-text --small --muted">
                {isFiltering
                  ? t("users.countFiltered", {
                      shown: shown.length,
                      total: users.length,
                    })
                  : count("users.count", users.length)}
              </span>
            )}
          </div>

          {ready && users.length === 0 && (
            <p className="ds-text --muted">{t("users.empty")}</p>
          )}

          {users.length > 0 && (
            <input
              type="search"
              className="ds-input"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("users.filterPlaceholder")}
              aria-label={t("users.filterLabel")}
            />
          )}

          {users.length > 0 && shown.length === 0 && (
            <p className="ds-text --muted app-empty">{t("users.noMatch")}</p>
          )}

          {shown.length > 0 && (
            <nav
              className="ds-menu app-side-menu"
              aria-label={t("users.listLabel")}
            >
              <ul>
                {shown.map((user) => (
                  <li key={user.email}>
                    <button
                      type="button"
                      className={`app-user-row${
                        user.email === selected ? " --selected" : ""
                      }`}
                      aria-current={
                        user.email === selected ? "true" : undefined
                      }
                      onClick={() => setSelected(user.email)}
                    >
                      <span className="app-menu-label app-truncate">
                        {user.displayName ?? user.email}
                      </span>
                      {user.displayName !== null && (
                        <span className="app-mono app-truncate">
                          {user.email}
                        </span>
                      )}
                      {user.adminSources.length > 0 && (
                        <span className="ds-cols --gap-xsmall">
                          <AdminSourceBadges sources={user.adminSources} />
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </section>

        <section className="ds-card">
          {current === null ? (
            <p className="ds-text --muted app-empty">
              {t("users.selectPrompt")}
            </p>
          ) : (
            <UserTeams api={api} user={current} canManage={isAdmin} />
          )}
        </section>
      </div>
    </div>
  );
}

function UserTeams({
  api,
  user,
  canManage,
}: {
  api: ApiClient;
  user: UserListing;
  canManage: boolean;
}) {
  const auth = useAuth();
  const { t, formatTimestamp } = useI18n();
  const [memberships, setMemberships] = useState<UserTeamMembership[]>([]);
  const [options, setOptions] = useState<{ id: string; label: string }[]>([]);
  const [choice, setChoice] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState<UserTeamMembership | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [teams, all] = await Promise.all([
        api.listUserTeams(user.email),
        api.listTeams(),
      ]);
      setMemberships(teams.items);
      // Indented by depth so two teams with the same name are tellable apart,
      // the same way the folder picker does it.
      setOptions(
        flattenForest(buildForest(all.items.map(toTeamNode))).map((entry) => ({
          id: entry.id,
          label: `${"　".repeat(entry.depth)}${entry.name}`,
        })),
      );
      setError(null);
    } catch (caught) {
      if (isSignedOutError(caught)) {
        auth.reportSignedOut();
        return;
      }
      setError(describeError(caught, t));
    } finally {
      setLoading(false);
    }
  }, [api, auth, t, user.email]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setBusy(true);
      setError(null);
      try {
        await action();
        await load();
        return true;
      } catch (caught) {
        if (isSignedOutError(caught)) {
          auth.reportSignedOut();
          return false;
        }
        setError(describeError(caught, t));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [auth, load, t],
  );

  const joined = new Set(memberships.map((membership) => membership.teamId));
  const joinable = options.filter((option) => !joined.has(option.id));

  return (
    <>
      <div className="ds-cols --middle --gap-small">
        <h2 className="ds-text --large">{user.displayName ?? user.email}</h2>
        <AdminSourceBadges sources={user.adminSources} />
      </div>

      {user.adminSources.includes("ALLOWLIST") && (
        <p className="ds-text --small --muted">{t("users.allowlistNote")}</p>
      )}
      <div className="ds-details">
        <dl>
          <dt>{t("users.email")}</dt>
          <dd>
            <div className="app-mono">{user.email}</div>
          </dd>
          <dt>{t("users.firstSignIn")}</dt>
          <dd>
            <div className="app-mono">{formatTimestamp(user.createdAt)}</div>
          </dd>
          <dt>{t("users.lastSignIn")}</dt>
          <dd>
            <div className="app-mono">{formatTimestamp(user.lastSignInAt)}</div>
          </dd>
        </dl>
      </div>

      {canManage && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (choice !== "") {
              void run(() => api.joinTeam(user.email, choice)).then((done) => {
                if (done) {
                  setChoice("");
                }
              });
            }
          }}
        >
          {/* SDS makes any form inside ds-rows a rows grid; the row lives inside it. */}
          <div className="ds-cols --nowrap --gap-xsmall">
            <select
              className="ds-select app-grow"
              value={choice}
              disabled={busy || joinable.length === 0}
              aria-label={t("users.teamToAdd")}
              onChange={(event) => setChoice(event.target.value)}
            >
              <option value="">
                {joinable.length === 0
                  ? t("users.noJoinableTeams")
                  : t("users.chooseTeam")}
              </option>
              {joinable.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="submit"
              className="ds-button --primary --icon-plus"
              disabled={busy || choice === ""}
            >
              <span>{t("users.addToTeam")}</span>
            </button>
          </div>
        </form>
      )}

      {error !== null && <Alert tone="failed">{error}</Alert>}

      {loading && <p className="ds-text --muted">{t("users.loading")}</p>}

      {!loading && memberships.length === 0 && (
        <p className="ds-text --muted">{t("users.noTeams")}</p>
      )}

      {memberships.length > 0 && (
        <ul className="app-list">
          {memberships.map((membership) => (
            <li
              key={membership.teamId}
              className="ds-cols --middle --nowrap --gap-small"
            >
              <span className="app-truncate app-grow">{membership.name}</span>
              <span className="ds-text --small --muted app-mono">
                {formatTimestamp(membership.addedAt)}
              </span>
              {canManage && (
                <button
                  type="button"
                  className="ds-button --xsmall --alert"
                  disabled={busy}
                  onClick={() => setLeaving(membership)}
                >
                  <span>{t("common.remove")}</span>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {leaving !== null && (
        <ConfirmDialog
          title={t("users.leaveTitle", { team: leaving.name })}
          description={t("users.leaveDescription", {
            email: user.email,
            team: leaving.name,
          })}
          confirmLabel={t("common.remove")}
          onConfirm={() => {
            setLeaving(null);
            void run(() => api.leaveTeam(user.email, leaving.teamId));
          }}
          onCancel={() => setLeaving(null)}
        />
      )}
    </>
  );
}
